/**
 * Instagram Feed Slider
 * Same drift-free design as the premium reviews slider: no cloned nodes,
 * "next" physically moves the first real card to the end after animating
 * one step left; "prev" moves the last real card to the start. Resting
 * position is always translateX(0%), so it can run forever without drift.
 */
(function () {
  'use strict';

  var BREAKPOINT_TABLET = 990;
  var BREAKPOINT_MOBILE = 750;

  function initSlider(root) {
    var scope = root.closest('.instagram-feed-wrap');
    var viewport = root.querySelector('[data-ig-viewport]');
    var track = root.querySelector('[data-ig-track]');
    var prevBtn = scope ? scope.querySelector('[data-ig-prev]') : null;
    var nextBtn = scope ? scope.querySelector('[data-ig-next]') : null;
    var dotsWrap = scope ? scope.querySelector('[data-ig-dots]') : null;

    if (!track) return;

    var realCount = track.children.length;
    if (realCount === 0) return;

    var settings = {
      autoplay: root.dataset.autoplay === 'true',
      autoplaySpeed: parseInt(root.dataset.autoplaySpeed, 10) || 4000,
      transitionSpeed: parseInt(root.dataset.transitionSpeed, 10) || 500,
      loop: root.dataset.loop === 'true',
      pauseOnHover: root.dataset.pauseHover === 'true',
      swipe: root.dataset.swipe === 'true',
      keyboard: root.dataset.keyboard === 'true',
      perViewDesktop: parseInt(root.dataset.perViewDesktop, 10) || 4,
      perViewTablet: parseInt(root.dataset.perViewTablet, 10) || 3,
      perViewMobile: parseInt(root.dataset.perViewMobile, 10) || 1
    };

    var perView = getPerView();
    var logicalIndex = 0;
    var clampedIndex = 0;
    var autoplayTimer = null;
    var isAnimating = false;
    var isDragging = false;
    var dragStartX = 0;
    var dragDelta = 0;
    var canSlide = realCount > perView;

    function getPerView() {
      var w = window.innerWidth;
      if (w < BREAKPOINT_MOBILE) return settings.perViewMobile;
      if (w < BREAKPOINT_TABLET) return settings.perViewTablet;
      return settings.perViewDesktop;
    }

    function stepPercent() { return 100 / perView; }

    function setTrackTransition(enabled) {
      track.style.transition = enabled
        ? 'transform ' + settings.transitionSpeed + 'ms ease'
        : 'none';
    }

    function forceReflow() {
      // eslint-disable-next-line no-unused-expressions
      track.offsetHeight;
    }

    function buildDots() {
      if (!dotsWrap || !canSlide) {
        if (dotsWrap) dotsWrap.innerHTML = '';
        return;
      }
      dotsWrap.innerHTML = '';
      var dotCount = settings.loop ? realCount : (realCount - perView + 1);
      for (var i = 0; i < dotCount; i++) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'ig-dot';
        dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        dot.addEventListener('click', function (idx) {
          return function () {
            stopAutoplay();
            goToDot(idx);
            startAutoplay();
          };
        }(i));
        dotsWrap.appendChild(dot);
      }
      updateDots();
    }

    function updateDots() {
      if (!dotsWrap) return;
      var active = settings.loop ? logicalIndex : clampedIndex;
      var dots = dotsWrap.querySelectorAll('.ig-dot');
      dots.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === active);
      });
    }

    function updateArrowState() {
      if (settings.loop || !canSlide) return;
      if (prevBtn) prevBtn.disabled = clampedIndex <= 0;
      if (nextBtn) nextBtn.disabled = clampedIndex >= realCount - perView;
    }

    function next() {
      if (!canSlide || isAnimating) return;

      if (!settings.loop) {
        if (clampedIndex >= realCount - perView) return;
        clampedIndex++;
        animateToClampedIndex();
        return;
      }

      isAnimating = true;
      setTrackTransition(true);
      track.style.transform = 'translateX(-' + stepPercent() + '%)';
      logicalIndex = (logicalIndex + 1) % realCount;
      updateDots();

      var done = function () {
        track.removeEventListener('transitionend', onEnd);
        clearTimeout(safety);
        var first = track.firstElementChild;
        track.appendChild(first);
        setTrackTransition(false);
        track.style.transform = 'translateX(0%)';
        forceReflow();
        isAnimating = false;
      };
      function onEnd(e) {
        if (e.target !== track || e.propertyName !== 'transform') return;
        done();
      }
      track.addEventListener('transitionend', onEnd);
      var safety = setTimeout(done, settings.transitionSpeed + 150);
    }

    function prev() {
      if (!canSlide || isAnimating) return;

      if (!settings.loop) {
        if (clampedIndex <= 0) return;
        clampedIndex--;
        animateToClampedIndex();
        return;
      }

      isAnimating = true;
      var last = track.lastElementChild;
      track.insertBefore(last, track.firstElementChild);
      logicalIndex = (logicalIndex - 1 + realCount) % realCount;
      updateDots();

      setTrackTransition(false);
      track.style.transform = 'translateX(-' + stepPercent() + '%)';
      forceReflow();

      requestAnimationFrame(function () {
        setTrackTransition(true);
        track.style.transform = 'translateX(0%)';

        var done = function () {
          track.removeEventListener('transitionend', onEnd);
          clearTimeout(safety);
          isAnimating = false;
        };
        function onEnd(e) {
          if (e.target !== track || e.propertyName !== 'transform') return;
          done();
        }
        track.addEventListener('transitionend', onEnd);
        var safety = setTimeout(done, settings.transitionSpeed + 150);
      });
    }

    function goToDot(targetIndex) {
      if (isAnimating) return;

      if (!settings.loop) {
        clampedIndex = Math.max(0, Math.min(realCount - perView, targetIndex));
        animateToClampedIndex();
        return;
      }

      var diff = ((targetIndex - logicalIndex) % realCount + realCount) % realCount;
      if (diff === 0) return;
      for (var i = 0; i < diff; i++) {
        track.appendChild(track.firstElementChild);
      }
      logicalIndex = targetIndex;
      setTrackTransition(false);
      track.style.transform = 'translateX(0%)';
      forceReflow();
      updateDots();
    }

    function animateToClampedIndex() {
      isAnimating = true;
      setTrackTransition(true);
      track.style.transform = 'translateX(-' + (clampedIndex * stepPercent()) + '%)';
      updateDots();
      updateArrowState();

      var done = function () {
        track.removeEventListener('transitionend', onEnd);
        clearTimeout(safety);
        isAnimating = false;
      };
      function onEnd(e) {
        if (e.target !== track || e.propertyName !== 'transform') return;
        done();
      }
      track.addEventListener('transitionend', onEnd);
      var safety = setTimeout(done, settings.transitionSpeed + 150);
    }

    function startAutoplay() {
      if (!settings.autoplay || !canSlide) return;
      stopAutoplay();
      autoplayTimer = setInterval(next, settings.autoplaySpeed);
    }

    function stopAutoplay() {
      if (autoplayTimer) {
        clearInterval(autoplayTimer);
        autoplayTimer = null;
      }
    }

    if (settings.pauseOnHover) {
      root.addEventListener('mouseenter', stopAutoplay);
      root.addEventListener('mouseleave', startAutoplay);
    }

    if (prevBtn) prevBtn.addEventListener('click', function () {
      stopAutoplay();
      prev();
      startAutoplay();
    });
    if (nextBtn) nextBtn.addEventListener('click', function () {
      stopAutoplay();
      next();
      startAutoplay();
    });

    if (settings.keyboard) {
      root.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight') { stopAutoplay(); next(); startAutoplay(); }
        if (e.key === 'ArrowLeft') { stopAutoplay(); prev(); startAutoplay(); }
      });
    }

    if (settings.swipe && canSlide) {
      var threshold = 40;
      var maxNudge = 60;

      function onDragStart(clientX) {
        if (isAnimating) return;
        isDragging = true;
        dragStartX = clientX;
        dragDelta = 0;
        stopAutoplay();
        setTrackTransition(false);
      }

      function onDragMove(clientX) {
        if (!isDragging) return;
        dragDelta = clientX - dragStartX;
        var clamped = Math.max(-maxNudge, Math.min(maxNudge, dragDelta));
        track.style.transform = 'translateX(' + clamped + 'px)';
      }

      function onDragEnd() {
        if (!isDragging) return;
        isDragging = false;
        setTrackTransition(false);
        track.style.transform = 'translateX(0%)';
        forceReflow();
        if (Math.abs(dragDelta) > threshold) {
          dragDelta < 0 ? next() : prev();
        }
        startAutoplay();
      }

      viewport.addEventListener('touchstart', function (e) {
        onDragStart(e.touches[0].clientX);
      }, { passive: true });
      viewport.addEventListener('touchmove', function (e) {
        onDragMove(e.touches[0].clientX);
      }, { passive: true });
      viewport.addEventListener('touchend', onDragEnd);

      viewport.addEventListener('mousedown', function (e) {
        e.preventDefault();
        onDragStart(e.clientX);
      });
      window.addEventListener('mousemove', function (e) {
        if (isDragging) onDragMove(e.clientX);
      });
      window.addEventListener('mouseup', onDragEnd);
    }

    function applyControlVisibility() {
      if (!canSlide) {
        if (prevBtn) prevBtn.style.display = 'none';
        if (nextBtn) nextBtn.style.display = 'none';
        if (dotsWrap) dotsWrap.style.display = 'none';
      } else {
        if (prevBtn) prevBtn.style.display = '';
        if (nextBtn) nextBtn.style.display = '';
        if (dotsWrap) dotsWrap.style.display = '';
      }
    }

    function rebuild() {
      var newPerView = getPerView();
      if (newPerView === perView) return;
      perView = newPerView;
      canSlide = realCount > perView;
      clampedIndex = 0;
      logicalIndex = 0;
      isAnimating = false;
      root.style.setProperty('--ig-per-view', perView);
      setTrackTransition(false);
      track.style.transform = 'translateX(0%)';
      forceReflow();
      buildDots();
      updateArrowState();
      applyControlVisibility();
    }

    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(rebuild, 150);
    });

    root.style.setProperty('--ig-per-view', perView);
    applyControlVisibility();
    buildDots();
    updateArrowState();
    if (canSlide) startAutoplay();
  }

  function initFadeUp() {
    var sections = document.querySelectorAll('.instagram-feed-section.ig-animate');
    if (!('IntersectionObserver' in window)) {
      sections.forEach(function (s) { s.classList.add('is-visible'); });
      return;
    }
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15 });
    sections.forEach(function (s) { observer.observe(s); });
  }

  function init() {
    document.querySelectorAll('.instagram-feed-section').forEach(function (section) {
      section.classList.add('ig-js-ready');
    });
    document.querySelectorAll('[data-ig-slider]').forEach(initSlider);
    initFadeUp();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var slider = e.target.querySelector && e.target.querySelector('[data-ig-slider]');
    if (slider) initSlider(slider);
    var section = e.target.querySelector && e.target.querySelector('.instagram-feed-section.ig-animate');
    if (section) section.classList.add('is-visible', 'ig-js-ready');
  });
})();